import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import {benefactorsApi} from '../api';
import {BenefactorsFilter, BenefactorWithMetaModel, CreateBenefactorDTO} from '../model';
import {useCallback, useMemo, useState} from 'react';
import {useNotifications} from '../../../shared';

type BenefactorsState = {
  getInfiniteBenefactors: UseInfiniteQueryResult<InfiniteData<BenefactorWithMetaModel>, Error>;
  filters: BenefactorsFilter;
  updateFilter: (newFilter: Partial<BenefactorsFilter>) => void;
  createBenefactorMutation: UseMutationResult<unknown, Error, CreateBenefactorDTO>;
};

const DEFAULT_PAGE_SIZE = 25;

export const useBenefactors = (initialFilter: BenefactorsFilter = {}): BenefactorsState => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<BenefactorsFilter>(() => ({
    pageSize: DEFAULT_PAGE_SIZE,
    ...initialFilter,
  }));
  const {createNotification} = useNotifications();

  const infiniteQueryFilters = useMemo(() => {
    const {after, ...rest} = filters;
    return rest;
  }, [filters]);

  const getInfiniteBenefactors = useInfiniteQuery<
    BenefactorWithMetaModel,
    Error,
    InfiniteData<BenefactorWithMetaModel>,
    ['benefactors', 'infinite', Omit<BenefactorsFilter, 'after'>],
    string | undefined
  >({
    queryKey: ['benefactors', 'infinite', infiniteQueryFilters],
    queryFn: ({pageParam}) => {
      return benefactorsApi.getBenefactors({...infiniteQueryFilters, pageSize: filters.pageSize, after: pageParam});
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage, _allPages, _lastPageParam) => {
      return lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined;
    },
    staleTime: 60 * 1000,
  });

  const updateFilter = useCallback((newFilter: Partial<BenefactorsFilter>) => {
    setFilters((prev) => {
      return {
        ...prev,
        ...newFilter,
      };
    });
  }, []);

  const createBenefactorMutation = useMutation({
    mutationFn: benefactorsApi.createBenefactor,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['benefactors', 'infinite'], refetchType: 'all'});
      createNotification({
        title: 'Пользователь успешно создан',
        name: 'createBenefactor',
        theme: 'success',
      });
    },
  });

  return {
    getInfiniteBenefactors,
    filters,
    updateFilter,
    createBenefactorMutation,
  };
};
